// src/storeOverview/index.js - the admin app's Store overview page. Bundled to dist/storeOverview.js (globalName
// "StoreOverview"), loaded by admin.html ONLY.
//
// Matt, 16 Oct 2026: "in admin - i want to be able to select a store and it gives me all current information and an
// AI overview and an option to e-mail to me. Reason - im thinking if im doing a store visit - i want to be able to
// click and bring up all current stats for the store". The agreed spec and API contract: one store, one period
// (since the last store visit by default, or 7 / 14 / 30 days), every section as a card, an AI overview on demand, an
// email to the signed-in account's own address, and (Head Office only) a button to talk to Jack about the store.
//
// Data (the Worker, every route scoped on the SERVER: Head Office any store, an area manager their own area's):
//   GET  /api/admin/store-overview?store=&period=[&week=]   the payload; each section { ok, data } or { ok:false, error }
//   POST /api/admin/store-overview/ai     { store, period, refresh? } -> { key_points, focus, model, cached, generated_at }
//   POST /api/admin/store-overview/email  { store, period, include_ai } -> { ok, to }
// week= (the rota card's previous and next week) is this page's ask of the GET: the server answers the rota section for
// that week. Calls go to DB.workerUrl() with the admin Bearer (a relative path 404s on Netlify, field lesson).
// The Store visits card (sections.visits: the last five visits' RAG and dates, the latest visit's comments and actions)
// opens a report through src/storeVisits' own opener (window.openStoreVisitDetail); admin.html's ?visit=<id> link
// comes through openVisitLink here, which checks the visit is one the person could open by clicking.
//
// One section failing never blanks the page: a card with ok:false says "Could not load: <error>" and the rest draw.
// D&G cases, concerns and exit interviews are NEVER part of this page (HR-confidential, Head Office only).
//
// The store list here is a courtesy (the server is the boundary): Head Office every store, an area manager
// AREA_STORE_MAP[their area], anyone else none. Grouped by area.
//
// Globals read by bare name at call time (admin.html / db.js): DB, currentAdmin, ALL_STORES, ALL_AREAS,
// AREA_STORE_MAP, getStoreArea, getStoreType, showToast, switchTab, StoreRotas (uiConfirm is imported, window.uiConfirm
// preferred when set). Every one is guarded.
//
// Colours are CSS variables only (docs/dark-mode.md). Names, store names and anything people wrote (actions, shout-outs,
// requests, product text, the AI's words) carry translate="no" (src/i18n). Nothing here decides from on-screen text.

import { uiConfirm as sharedConfirm } from '../shared/confirm-dialog.js';   // the Shift are-you-sure box (v134.2)
import { coverAwareAreaStores } from '../shared/area-cover.js';   // v136.9: own area plus any area handed over for today

const LS_KEY = 'shift_admin_store_overview_store';
export const PERIODS = [
  { key: 'last_visit', label: 'Since last visit' },
  { key: '7', label: '7 days' },
  { key: '14', label: '14 days' },
  { key: '30', label: '30 days' },
];
// The cards, in the contract's order (the AI overview is drawn first, on its own).
export const SECTION_ORDER = [
  ['sales', 'Sales'],
  ['addons', 'Add-ons'],
  ['scorecard', 'Scorecard'],
  ['shift_compliance', 'Shift compliance'],
  ['rota', 'Rota'],
  ['holidays', 'Staff holidays'],
  ['staff', 'Staff'],
  ['training', 'Training'],
  ['inductions', 'Inductions'],
  ['reviews', 'Reviews'],
  ['visits', 'Store visits'],
  ['virtual_store', 'Virtual Store'],
  ['customers', 'Customers'],
  ['stock', 'Stock'],
  ['customer_requests', 'Customer requests'],
  ['petty_cash', 'Petty cash'],
  ['cover', 'Cover'],
];
// A store visit's id (a UUID), the shape admin.html's ?visit= link already accepts.
export const VISIT_ID_RE = /^[0-9a-f-]{16,60}$/i;
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

let _store = null;      // the store on screen
let _period = 'last_visit';
let _data = null;       // the last GET answer for _store + _period
let _week = null;       // the rota week asked for (null = the server's default, this week)
let _seq = 0;           // the newest load; an older answer that lands late is dropped
let _ai = null;         // { key, key_points, focus, cached, generated_at } for the store + period on screen
let _aiBusy = false;
let _emailBusy = false;
// v136.8 (the 16 Oct 2026 render audit): opening the tab always asked the Worker again. Now an answer for the
// same store, period, rota week and person under FRESH_MS old is drawn again as it is; Refresh always asks.
const FRESH_MS = 2 * 60 * 1000;
let _loadedKey = null;   // what _data answers (loadKey())
let _loadedAt = 0;       // when it arrived

// ── Small pure helpers ──────────────────────────────────────────────────────
function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function nameHtml(s) { return '<span translate="no">' + esc(s) + '</span>'; }
function isNum(n) { return n !== null && n !== undefined && n !== '' && isFinite(Number(n)); }
export function fmtNum(n, dp) {
  if (!isNum(n)) return '-';
  const v = Number(n);
  const fixed = (dp ? v.toFixed(dp) : String(Math.round(v)));
  const parts = fixed.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}
export function fmtMoney(n, currency) {
  if (!isNum(n)) return '-';
  return (currency === 'QAR' ? 'QAR' : 'AED') + ' ' + fmtNum(n);
}
export function fmtPct(n) { return isNum(n) ? Math.round(Number(n)) + '%' : '-'; }
// 'YYYY-MM-DD' -> a local Date (never a UTC parse), or null.
export function parseYmd(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ''));
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}
export function ymd(d) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}
// 'Mon 12 Oct': the shape the Arabic catalogue already rebuilds (src/i18n/ar/admin-rota.js).
export function dayLabel(s) {
  const d = parseYmd(s);
  return d ? DAY_NAMES[d.getDay()] + ' ' + d.getDate() + ' ' + MONTHS[d.getMonth()] : String(s || '');
}
export function weekLabel(s) {
  const d = parseYmd(s);
  return d ? 'Week commencing ' + DAY_NAMES[d.getDay()] + ' ' + d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear() : '';
}
// The week `dir` weeks from `weekStart` ('YYYY-MM-DD'), local date maths.
export function shiftWeek(weekStart, dir) {
  const d = parseYmd(weekStart);
  if (!d) return null;
  d.setDate(d.getDate() + 7 * (parseInt(dir, 10) || 0));
  return ymd(d);
}
// A percentage against its target: green at or over, amber within 10 points, red below.
export function pctTone(p) {
  if (!isNum(p)) return '';
  const v = Number(p);
  return v >= 100 ? 'ok' : v >= 90 ? 'warn' : 'bad';
}
const RAG_TONE = { g: 'ok', a: 'warn', r: 'bad' };
const RAG_WORD = { g: 'Green', a: 'Amber', r: 'Red' };
function chip(tone, text) {
  return '<span class="ui-chip so-chip-' + (tone || 'none') + '">' + esc(text) + '</span>';
}
function pctChip(p) { return isNum(p) ? chip(pctTone(p), fmtPct(p)) : '-'; }
function ragChip(rag, emptyWord) {
  const r = String(rag || '').toLowerCase().charAt(0);
  return RAG_TONE[r] ? chip(RAG_TONE[r], RAG_WORD[r]) : (emptyWord ? chip('none', emptyWord) : '-');
}

// ── Pure: which stores this person may look at ─────────────────────────────
// Head Office (role 'admin', the isHeadOffice rule) every real store; an area manager their area's; anyone else
// none (the server answers them 403). isVirtual(name) drops "stores" with no shop floor.
export function storesInScope(admin, allStores, areaMap, isVirtual) {
  if (!admin) return [];
  const role = String(admin.role || '').toLowerCase();
  const virt = typeof isVirtual === 'function' ? isVirtual : () => false;
  const all = (Array.isArray(allStores) ? allStores : [])
    .map((s) => (typeof s === 'string' ? s : (s && s.name)))
    .filter((s) => typeof s === 'string' && s && !virt(s));
  if (role === 'admin') return all;
  if (role === 'area_manager') {
    const mine = coverAwareAreaStores(admin, areaMap);   // v136.9: + any area handed over for today
    return all.filter((s) => mine.indexOf(s) !== -1);
  }
  return [];
}
// Grouped by area, in the areas' own order; a store with no area goes last under "Other".
// areaOf(store) -> area id; areas = [{ id, name }].
export function groupByArea(stores, areaOf, areas) {
  const list = Array.isArray(areas) ? areas : [];
  const byId = {};
  const order = [];
  (stores || []).forEach((s) => {
    let a = '';
    try { a = String((typeof areaOf === 'function' ? areaOf(s) : '') || ''); } catch (e) { a = ''; }
    if (!byId[a]) { byId[a] = []; order.push(a); }
    byId[a].push(s);
  });
  const rank = (id) => {
    if (id === '') return 1e9;
    const i = list.findIndex((x) => x && String(x.id) === id);
    return i === -1 ? 1e8 : i;
  };
  return order.slice().sort((x, y) => rank(x) - rank(y)).map((id) => {
    const f = list.find((x) => x && String(x.id) === id);
    return { label: id === '' ? 'Other' : ((f && f.name) ? String(f.name) : 'Area ' + id), stores: byId[id] };
  });
}
// The Head Office rule (currentAdmin.role === 'admin'), the same as the server's isHeadOffice.
export function isHeadOffice(admin) { return !!(admin && String(admin.role || '').toLowerCase() === 'admin'); }
// May this person open a visit at `store`? The Store visits list's own rule (src/storeVisits scopedStores): Head
// Office any store, an area manager their area's, a store manager their own store, anyone else none.
export function visitInScope(store, adminObj, areaMap) {
  if (!adminObj || !store) return false;
  const role = String(adminObj.role || '').toLowerCase();
  if (role === 'admin') return true;
  if (role === 'area_manager') return coverAwareAreaStores(adminObj, areaMap).indexOf(store) !== -1;   // v136.9: + hand-overs
  if (role === 'store_manager') return adminObj.store === store;
  return false;
}
// The question the "Talk to Jack" button leaves ready in Jack's box.
export function jackQuestion(store) {
  return 'Tell me about ' + store + ': what should I focus on during my visit?';
}

// ── The globals ─────────────────────────────────────────────────────────────
function admin() { try { return (typeof currentAdmin !== 'undefined') ? currentAdmin : null; } catch (e) { return null; } }
function myStores() {
  let areaMap = {};
  try { areaMap = (typeof AREA_STORE_MAP !== 'undefined') ? AREA_STORE_MAP : {}; } catch (e) { areaMap = {}; }
  return storesInScope(
    admin(),
    (typeof ALL_STORES !== 'undefined') ? ALL_STORES : [],
    areaMap,
    (s) => { try { return typeof getStoreType === 'function' && getStoreType(s) === 'virtual'; } catch (e) { return false; } },
  );
}
function areaOf(s) { try { return (typeof getStoreArea === 'function') ? getStoreArea(s) : ''; } catch (e) { return ''; } }
function remembered() { try { return localStorage.getItem(LS_KEY); } catch (e) { return null; } }
function remember(s) { try { localStorage.setItem(LS_KEY, s); } catch (e) { /* private window: just not remembered */ } }
function toast(msg, type) { try { if (typeof showToast === 'function') showToast(msg, type || ''); } catch (e) { /* no toast host */ } }
function root() { return (typeof document !== 'undefined') ? document.getElementById('storeOverviewRoot') : null; }
function byId(id) { return (typeof document !== 'undefined') ? document.getElementById(id) : null; }
// The hidden tab bar's button for a tab, matched on its switchTab('name' call (as admin.html's ?screen= link does).
function tabButton(name) {
  if (typeof document === 'undefined') return null;
  const want = "switchTab('" + name + "'";
  return Array.from(document.querySelectorAll('#mainTabs .tab')).find((el) => (el.getAttribute('onclick') || '').indexOf(want) !== -1) || null;
}

async function api(path, opts) {
  const o = opts || {};
  if (typeof DB === 'undefined' || !DB || typeof DB.workerUrl !== 'function') throw new Error('The app has not finished loading. Refresh the page and try again.');
  const token = (typeof DB.getToken === 'function') ? DB.getToken() : null;
  const headers = {};
  if (token) headers['Authorization'] = 'Bearer ' + token;
  if (o.body) headers['Content-Type'] = 'application/json';
  const res = await fetch(DB.workerUrl() + path, { cache: 'no-store', method: o.method || 'GET', headers, body: o.body ? JSON.stringify(o.body) : undefined });
  let j = null;
  try { j = await res.json(); } catch (e) { j = null; }
  if (!res.ok) {
    if (res.status === 401) throw new Error('Your session has expired. Sign out and back in.');
    if (res.status === 403) throw new Error((j && j.error) || 'This store is not in your scope.');
    throw new Error((j && j.error) || ('The server answered ' + res.status + '.'));
  }
  return j || {};
}

// ── Rendering (variables only, docs/dark-mode.md) ───────────────────────────
const CSS = '#storeOverviewRoot .so-bar{display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:10px}' +
  '#storeOverviewRoot select.so-store{padding:8px;border:1px solid var(--border-strong);border-radius:8px;background:var(--input-bg);color:var(--text);font:inherit;max-width:100%}' +
  '#storeOverviewRoot .so-period{padding:6px 12px;border:1px solid var(--border-strong);border-radius:999px;background:var(--card);color:var(--text);font:inherit;font-size:13px;cursor:pointer}' +
  '#storeOverviewRoot .so-period[aria-pressed="true"]{background:var(--ok-bg);color:var(--ok-deep);border-color:var(--ok-deep);font-weight:700}' +
  '#storeOverviewRoot .so-meta{font-size:12px;color:var(--sub);margin-bottom:12px;display:flex;gap:6px;flex-wrap:wrap}' +
  '#storeOverviewRoot .so-card{background:var(--card);border:1px solid var(--border);border-radius:12px;padding:14px 16px;margin-bottom:12px;color:var(--text);min-width:0}' +
  '#storeOverviewRoot .so-h{font-size:15px;font-weight:800;margin:0 0 10px;display:flex;align-items:center;gap:8px;flex-wrap:wrap}' +
  '#storeOverviewRoot .so-sub{font-size:13px;font-weight:700;margin:12px 0 6px;color:var(--text)}' +
  '#storeOverviewRoot .so-note{font-size:12px;color:var(--sub)}' +
  '#storeOverviewRoot .so-err{background:var(--danger-bg);color:var(--danger-deep);border-radius:8px;padding:8px 10px;font-size:13px}' +
  '#storeOverviewRoot .so-scroll{overflow-x:auto;-webkit-overflow-scrolling:touch;max-width:100%}' +
  '#storeOverviewRoot table.so-t{border-collapse:collapse;width:100%;font-size:12.5px;color:var(--text)}' +
  '#storeOverviewRoot table.so-t th{background:var(--surface-2);color:var(--sub);font-weight:700;padding:6px 8px;border-bottom:1px solid var(--border);text-align:start;white-space:nowrap}' +
  '#storeOverviewRoot table.so-t td{padding:6px 8px;border-bottom:1px solid var(--border);white-space:nowrap;vertical-align:top}' +
  '#storeOverviewRoot table.so-t td.so-wrap{white-space:normal;min-width:180px}' +
  '#storeOverviewRoot .so-stats{display:flex;gap:10px;flex-wrap:wrap}' +
  '#storeOverviewRoot .so-stat{background:var(--surface-2);border:1px solid var(--border);border-radius:10px;padding:8px 12px;min-width:110px}' +
  '#storeOverviewRoot .so-k{font-size:11.5px;color:var(--sub)}' +
  '#storeOverviewRoot .so-v{font-size:17px;font-weight:800}' +
  '#storeOverviewRoot .so-chip-ok{background:var(--ok-bg);color:var(--ok-deep)}' +
  '#storeOverviewRoot .so-chip-warn{background:var(--warn-bg);color:var(--warn-deep)}' +
  '#storeOverviewRoot .so-chip-bad{background:var(--danger-bg);color:var(--danger-deep)}' +
  '#storeOverviewRoot .so-chip-info{background:var(--info-bg);color:var(--info-deep)}' +
  '#storeOverviewRoot .so-chip-none{background:var(--surface-3);color:var(--text)}' +
  '#storeOverviewRoot ul.so-list{margin:0;padding-inline-start:20px;font-size:13.5px;line-height:1.55}' +
  '#storeOverviewRoot .so-pill{display:inline-block;padding:3px 7px;border-radius:6px;font-size:11px;font-weight:700;white-space:nowrap}' +
  '#storeOverviewRoot .so-actions{display:flex;gap:8px;flex-wrap:wrap;align-items:center}' +
  '#storeOverviewRoot .so-visit{display:inline-flex;align-items:center;gap:4px;font-size:12px;padding:2px 8px 2px 2px;border:1px solid var(--border);border-radius:999px;background:var(--surface-2)}' +
  '#storeOverviewRoot .so-foot{display:flex;justify-content:center;margin:6px 0 18px}';
function ensureCss() {
  if (typeof document === 'undefined' || document.getElementById('storeOverviewCss')) return;
  const st = document.createElement('style');
  st.id = 'storeOverviewCss';
  st.textContent = CSS;
  document.head.appendChild(st);
}

function card(key, title, body, extraHead) {
  return '<section class="so-card" data-sec="' + esc(key) + '"><h3 class="so-h"><span>' + esc(title) + '</span>' + (extraHead || '') + '</h3>' + body + '</section>';
}
function errBox(msg) {
  return '<div class="so-err" role="alert">' + esc('Could not load: ' + (msg || 'no reason given')) + '</div>';
}
function none() { return '<div class="so-note">None.</div>'; }
function stat(label, valueHtml) {
  return '<div class="so-stat"><div class="so-k">' + esc(label) + '</div><div class="so-v">' + valueHtml + '</div></div>';
}
// A table: heads = English labels; rows = arrays of cell HTML (already escaped).
function table(heads, rows) {
  if (!rows || !rows.length) return none();
  return '<div class="so-scroll"><table class="so-t"><thead><tr>' + heads.map((h) => '<th>' + esc(h) + '</th>').join('') +
    '</tr></thead><tbody>' + rows.map((r) => '<tr>' + r.map((c) => (typeof c === 'object' && c && c.wrap ? '<td class="so-wrap">' + c.html + '</td>' : '<td>' + c + '</td>')).join('') + '</tr>').join('') +
    '</tbody></table></div>';
}
function namesList(list) {
  const arr = (Array.isArray(list) ? list : []).filter((x) => x && x.name);
  if (!arr.length) return none();
  return '<div style="font-size:13.5px;line-height:1.6">' + arr.map((x) => nameHtml(x.name)).join(', ') + '</div>';
}
function sub(title, count) {
  return '<div class="so-sub"><span>' + esc(title) + '</span>' + (count == null ? '' : ' <span class="ui-chip so-chip-none">' + esc(String(count)) + '</span>') + '</div>';
}
const arr = (x) => (Array.isArray(x) ? x : []);

// ── The section bodies (data -> HTML). Each one copes with a missing field. ──
const SALES_LABEL = { yesterday: 'Yesterday', wtd: 'Week to date', mtd: 'Month to date', ytd: 'Year to date' };
const ADDON_LABEL = { demo: 'Demos', batteries: 'Batteries', wrap: 'Gift wrap', bags: 'Bags', cards: 'Cards' };
const CELL_STYLE = {
  work: 'background:var(--ok-bg);color:var(--ok-deep)',
  off: 'background:var(--danger-bg);color:var(--danger-deep)',
  hol: 'background:var(--warn-bg);color:var(--warn-deep)',
  sick: 'background:var(--danger-bg);color:var(--danger-deep)',
  toil: 'background:var(--surface-3);color:var(--text)',
  apl: 'background:var(--surface-3);color:var(--text)',
  away: 'background:var(--info-bg);color:var(--info-deep)',
  oos: 'background:var(--info-bg);color:var(--info-deep)',
  none: 'color:var(--sub)',
};
const COVER_STATUS = { short: ['bad', 'Short'], spare: ['info', 'Spare'], ok: ['ok', 'OK'], none: ['none', 'No data'] };

export const RENDER = {
  sales(d, ctx) {
    return table(['Period', 'Sales', 'Target', '% of target', 'Last year', '% of last year'], arr(d.windows).map((w) => [
      '<div>' + esc(SALES_LABEL[w.key] || w.label || w.key) + '</div><div class="so-note">' + esc(dayLabel(w.from)) + (w.to && w.to !== w.from ? ' - ' + esc(dayLabel(w.to)) : '') + '</div>',
      esc(fmtMoney(w.sales, ctx.currency)), esc(fmtMoney(w.target, ctx.currency)), pctChip(w.pct_target),
      esc(fmtMoney(w.ly, ctx.currency)), pctChip(w.pct_ly),
    ]));
  },
  addons(d) {
    return (isNum(d.blended_pct) ? '<div class="so-stats" style="margin-bottom:10px">' + stat('Blended', pctChip(d.blended_pct)) + '</div>' : '') +
      table(['Add-on', 'Actual', 'Target', '% of target'], arr(d.lines).map((l) => [
        esc(ADDON_LABEL[l.key] || l.label || l.key), esc(fmtNum(l.actual, 1)), esc(fmtNum(l.target, 1)), pctChip(l.pct),
      ]));
  },
  scorecard(d) {
    return table(['Measure', 'Value', 'Target', '% of target', 'RAG'], arr(d.lines).map((l) => [
      esc(l.label || l.key), esc(fmtNum(l.value, 1)), esc(fmtNum(l.target, 1)), pctChip(l.pct), ragChip(l.rag),
    ]));
  },
  shift_compliance(d) {
    const done = (x, label) => {
      const o = x || {};
      const pct = isNum(o.expected) && Number(o.expected) > 0 ? (Number(o.done) / Number(o.expected)) * 100 : null;
      return sub(label) + '<div class="so-stats" style="margin-bottom:6px">' + stat('Done', esc(fmtNum(o.done))) + stat('Expected', esc(fmtNum(o.expected))) + stat('Completion', pctChip(pct)) + '</div>' +
        (arr(o.missed).length ? table(['Date', 'Missed'], arr(o.missed).map((m) => [esc(dayLabel(m.date)), esc(m.name)])) : '');
    };
    return sub('Late clock-ins', arr(d.late_clockins).length) +
      table(['Name', 'Date', 'Minutes late', 'Clocked in', 'Rota start'], arr(d.late_clockins).map((x) => [
        nameHtml(x.name), esc(dayLabel(x.date)), esc(fmtNum(x.minutes_late)), esc(x.clock_in || '-'), esc(x.rota_start || '-')])) +
      sub('Break issues', arr(d.break_issues).length) +
      table(['Name', 'Date', 'Detail'], arr(d.break_issues).map((x) => [nameHtml(x.name), esc(dayLabel(x.date)), { wrap: true, html: esc(x.detail) }])) +
      sub('Clocked in away from the store', arr(d.location_off).length) +
      table(['Name', 'Date', 'Detail'], arr(d.location_off).map((x) => [nameHtml(x.name), esc(dayLabel(x.date)), { wrap: true, html: esc(x.detail) }])) +
      sub('Shifts never clocked out', arr(d.open_shifts).length) +
      table(['Name', 'Date', 'Clocked in'], arr(d.open_shifts).map((x) => [nameHtml(x.name), esc(dayLabel(x.date)), esc(x.clock_in || '-')])) +
      sub('Never signed in', arr(d.never_signed_in).length) + namesList(d.never_signed_in) +
      done(d.checklists, 'Checklists') + done(d.logs, 'Store logs');
  },
  rota(d) {
    const days = arr(d.days);
    const rows = arr(d.rows);
    let h = '<div class="so-actions" style="margin-bottom:8px">' +
      '<button type="button" class="btn btn-outline btn-sm" onclick="StoreOverview.rotaWeek(-1)">&lsaquo; Previous</button>' +
      '<span style="font-size:13px;font-weight:700">' + esc(weekLabel(d.week_start)) + '</span>' +
      '<button type="button" class="btn btn-outline btn-sm" onclick="StoreOverview.rotaWeek(1)">Next &rsaquo;</button>' +
      '<button type="button" class="btn btn-outline btn-sm" onclick="StoreOverview.openInStoreRotas()">Open in Store rotas</button></div>';
    if (!rows.length) return h + '<div class="so-note">No rota for this week yet.</div>';
    h += '<div class="so-scroll"><table class="so-t"><thead><tr><th>Staff</th>' + days.map((x) => '<th>' + esc(dayLabel(x)) + '</th>').join('') + '<th>Hours</th></tr></thead><tbody>';
    rows.forEach((r) => {
      const cells = arr(r.cells);
      h += '<tr><td>' + nameHtml(r.name) + (r.role ? '<div class="so-note">' + esc(r.role) + '</div>' : '') +
        (r.cover_from ? '<div><span class="ui-chip so-chip-warn">Cover</span> <span class="so-note" translate="no">' + esc(r.cover_from) + '</span></div>' : '') + '</td>';
      days.forEach((day, i) => {
        const c = cells.find((x) => x && x.date === day) || cells[i] || { kind: 'none', text: '-' };
        const st = CELL_STYLE[c.kind] || CELL_STYLE.none;
        h += '<td style="text-align:center"><span class="so-pill" style="' + st + '">' + esc(c.text == null || c.text === '' ? '-' : c.text) + '</span></td>';
      });
      h += '<td>' + (isNum(r.hours) && Number(r.hours) > 0 ? esc(fmtNum(r.hours, 1)) + 'h' : '-') + '</td></tr>';
    });
    h += '<tr><td style="font-weight:700">Heads</td>' + days.map((x, i) => '<td style="text-align:center;font-weight:700">' + esc(isNum(arr(d.heads)[i]) ? String(arr(d.heads)[i]) : '-') + '</td>').join('') + '<td></td></tr>';
    return h + '</tbody></table></div>';
  },
  holidays(d) {
    return sub('Off this week', arr(d.off_this_week).length) +
      table(['Name', 'Days'], arr(d.off_this_week).map((x) => [nameHtml(x.name), { wrap: true, html: arr(x.dates).map((y) => esc(dayLabel(y))).join(', ') }])) +
      sub('Coming up', arr(d.upcoming).length) +
      table(['Name', 'From', 'To', 'Days'], arr(d.upcoming).map((x) => [nameHtml(x.name), esc(dayLabel(x.from)), esc(dayLabel(x.to)), esc(fmtNum(x.days, 1))])) +
      sub('Holiday left') +
      table(['Name', 'Days left', ''], arr(d.balances).map((x) => [nameHtml(x.name), esc(fmtNum(x.left, 1)), x.flag ? chip('warn', '15+ days left') : '']));
  },
  staff(d) {
    return '<div class="so-stats" style="margin-bottom:8px">' + stat('Headcount', esc(fmtNum(d.headcount))) + '</div>' +
      (arr(d.by_role).length ? '<div class="so-actions" style="margin-bottom:8px">' + arr(d.by_role).map((r) => '<span class="ui-chip so-chip-none"><span>' + esc(r.role) + '</span> <span>' + esc(fmtNum(r.count)) + '</span></span>').join('') + '</div>' : '') +
      table(['Name', 'Role', 'Holiday left', 'TOIL', ''], arr(d.people).map((p) => [
        nameHtml(p.name), esc(p.role || '-'), esc(fmtNum(p.holiday_left, 1)), esc(fmtNum(p.toil_balance, 1)), p.negative ? chip('bad', 'Negative balance') : '',
      ]));
  },
  training(d) {
    return '<div class="so-stats" style="margin-bottom:8px">' + stat('This week', pctChip(d.this_week_pct)) + '</div>' +
      (arr(d.weeks).length ? table(['Week', 'Done'], arr(d.weeks).map((w) => [esc(dayLabel(w.week_start)), pctChip(w.pct)])) : '') +
      sub('Not done this week', arr(d.not_done).length) + namesList(d.not_done);
  },
  inductions(d) {
    return '<div class="so-stats" style="margin-bottom:8px">' + stat('Complete', pctChip(d.pct)) + '</div>' +
      sub('In progress', arr(d.in_progress).length) +
      table(['Name', 'Done'], arr(d.in_progress).map((x) => [nameHtml(x.name), pctChip(x.pct)])) +
      sub('Not started', arr(d.not_started).length) + namesList(d.not_started);
  },
  reviews(d) {
    return '<div class="so-stats" style="margin-bottom:8px">' + (d.quarter ? stat('Quarter', '<span translate="no">' + esc(d.quarter) + '</span>') : '') +
      stat('Done', esc(fmtNum(d.done)) + ' / ' + esc(fmtNum(d.total))) + '</div>' +
      sub('Outstanding', arr(d.outstanding).length) + namesList(d.outstanding);
  },
  // The last five visits (newest first) as RAG pills with their dates (the word, never colour alone), then the
  // latest visit: its comments by section, its actions, and "Open this visit" (the Store visits report).
  visits(d) {
    const hist = arr(d.history);
    const latest = d.latest || null;
    if (!hist.length && !latest) return '<div class="so-note">No store visit on record.</div>';
    let h = '';
    if (hist.length) {
      h += sub('Last visits') + '<div class="so-actions" style="margin-bottom:8px">' + hist.map((v) =>
        '<span class="so-visit">' + ragChip(v.rag, 'No rating') + ' <span>' + esc(dayLabel(v.date)) + '</span></span>').join('') + '</div>';
    }
    if (!latest) return h;
    h += sub('Latest visit') + '<div class="so-stats" style="margin-bottom:8px">' + stat('Date', esc(dayLabel(latest.date))) + stat('RAG', ragChip(latest.rag)) +
      (latest.by ? stat('By', nameHtml(latest.by)) : '') + stat('Actions outstanding', esc(fmtNum(latest.actions_outstanding))) + '</div>';
    // Comments, grouped by the form's section (kept in the order they first appear).
    const groups = [];
    arr(latest.comments).forEach((c) => {
      if (!c || !c.text) return;
      const name = String(c.section || '');
      let g = groups.find((x) => x.name === name);
      if (!g) { g = { name, items: [] }; groups.push(g); }
      g.items.push(c.text);
    });
    h += sub('Comments');
    h += groups.length ? groups.map((g) => (g.name ? '<div class="so-note" style="font-weight:700;margin-top:6px" translate="no">' + esc(g.name) + '</div>' : '') +
      '<ul class="so-list">' + g.items.map((t) => '<li translate="no">' + esc(t) + '</li>').join('') + '</ul>').join('') : none();
    h += sub('Actions', arr(latest.actions).length) +
      (arr(latest.actions).length ? '<ul class="so-list">' + arr(latest.actions).map((a) => '<li>' + (a.done ? chip('ok', 'Done') : chip('warn', 'Still open')) + ' <span translate="no">' + esc(a.text) + '</span></li>').join('') + '</ul>' : none());
    if (VISIT_ID_RE.test(String(latest.id || ''))) {
      h += '<div class="so-actions" style="margin-top:10px"><button type="button" class="btn btn-outline btn-sm" onclick="StoreOverview.openVisit(\'' + esc(latest.id) + '\')">Open this visit</button></div>';
    }
    return h;
  },
  virtual_store(d) {
    return '<div class="so-stats" style="margin-bottom:8px">' + stat('Players', esc(fmtNum(d.players)) + ' / ' + esc(fmtNum(d.headcount))) +
      stat('Points this month', esc(fmtNum(d.points_month))) + stat('Minutes this month', esc(fmtNum(d.minutes_month))) +
      stat('League position', esc(fmtNum(d.league_rank)) + ' / ' + esc(fmtNum(d.league_size))) + stat('Quiz modules passed', esc(fmtNum(d.quiz_modules_passed))) + '</div>' +
      sub('Top players') + table(['Name', 'Points'], arr(d.top).map((x) => [nameHtml(x.name), esc(fmtNum(x.points))])) +
      sub('Not played yet', arr(d.not_played).length) + namesList(d.not_played);
  },
  customers(d) {
    return '<div class="so-stats" style="margin-bottom:8px">' + stat('Feedback score', esc(fmtNum(d.feedback_score, 1))) + stat('Feedback responses', esc(fmtNum(d.feedback_count))) +
      stat('Google rating', esc(fmtNum(d.google_rating, 1))) + stat('Google reviews', esc(fmtNum(d.google_reviews))) + stat('New Google reviews', esc(fmtNum(d.google_gain))) + '</div>' +
      sub('Shout-outs', arr(d.shoutouts).length) +
      (arr(d.shoutouts).length ? '<ul class="so-list">' + arr(d.shoutouts).map((s) => '<li><span translate="no">' + esc(s.text) + '</span> <span class="so-note">' + esc(isNum(s.rating) ? s.rating + '/10' : '') + ' ' + esc(dayLabel(s.date)) + '</span></li>').join('') + '</ul>' : none());
  },
  stock(d) {
    return '<div class="so-stats" style="margin-bottom:8px">' + stat('Top 50 lines out of stock', esc(String(arr(d.stockouts).length))) + stat('Lines with no sales', esc(fmtNum(d.zero_sale_lines))) +
      stat('Business average', esc(fmtNum(d.business_avg_zero_sale_lines, 1))) + '</div>' +
      table(['Code', 'Description'], arr(d.stockouts).map((x) => ['<span translate="no">' + esc(x.code) + '</span>', { wrap: true, html: '<span translate="no">' + esc(x.description) + '</span>' }]));
  },
  customer_requests(d) {
    return '<div class="so-stats" style="margin-bottom:8px">' + stat('Open requests', esc(fmtNum(d.open))) + '</div>' +
      table(['Item', 'Date'], arr(d.items).map((x) => [{ wrap: true, html: '<span translate="no">' + esc(x.item) + '</span>' }, esc(dayLabel(x.date))]));
  },
  petty_cash(d, ctx) {
    return '<div class="so-stats">' + stat('Waiting for approval', esc(fmtNum(d.pending))) + stat('Total waiting', esc(fmtMoney(d.total_pending, ctx.currency))) + '</div>';
  },
  cover(d) {
    return table(['Day', 'Cover', 'Short hours', 'Spare hours'], arr(d.days).map((x) => {
      const s = COVER_STATUS[x.status] || COVER_STATUS.none;
      return [esc(dayLabel(x.date)), chip(s[0], s[1]), esc(fmtNum(x.short_hours, 1)), esc(fmtNum(x.spare_hours, 1))];
    }));
  },
};

// One card for one section of the payload. A missing section or ok:false is "Could not load".
export function sectionHtml(key, title, sec, ctx) {
  if (!sec || typeof sec !== 'object') return card(key, title, errBox('the server did not send this section'));
  if (sec.ok === false) return card(key, title, errBox(sec.error));
  let body;
  try { body = RENDER[key](sec.data == null ? {} : sec.data, ctx || {}); }
  catch (e) { body = errBox((e && e.message) || 'the data could not be drawn'); }
  return card(key, title, body);
}

// The AI card: the two lists, or the button.
export function aiCardHtml(ai, busy) {
  const note = '<div class="so-note" style="margin-top:8px">Only numbers go to the AI, never names.</div>';
  if (busy) {
    return card('ai', 'AI overview', '<div aria-busy="true"><span class="ui-skel" style="width:70%;height:14px;margin:0 0 8px"></span>' +
      '<span class="ui-skel" style="width:90%;height:14px;margin:0 0 8px"></span><span class="ui-skel" style="width:60%;height:14px"></span></div>' + note);
  }
  if (!ai) {
    return card('ai', 'AI overview', '<div class="so-actions"><button type="button" class="btn btn-primary" onclick="StoreOverview.writeAi()">Write the overview</button></div>' + note);
  }
  if (ai.error) {
    return card('ai', 'AI overview', errBox(ai.error) + '<div class="so-actions" style="margin-top:8px"><button type="button" class="btn btn-primary" onclick="StoreOverview.writeAi()">Write the overview</button></div>' + note);
  }
  const list = (items) => (arr(items).length ? '<ul class="so-list" translate="no">' + arr(items).map((b) => '<li>' + esc(b) + '</li>').join('') + '</ul>' : none());
  const when = ai.generated_at ? fmtWhen(ai.generated_at) : '';
  return card('ai', 'AI overview',
    sub('Key points') + list(ai.key_points) + sub('Focus on the visit') + list(ai.focus) +
    '<div class="so-actions" style="margin-top:10px"><button type="button" class="btn btn-outline btn-sm" onclick="StoreOverview.refreshAi()">Refresh</button>' +
    (when ? '<span class="so-note"><span>Written</span> ' + when + '</span>' : '') + '</div>' + note);
}
function fmtWhen(iso) {
  const t = Date.parse(iso);
  if (isNaN(t)) return '';
  const d = new Date(t);
  // Two pieces, so 'Mon 12 Oct' is translated on its own: '<span>Mon 12 Oct</span> <span>14:05</span>'.
  return '<span>' + DAY_NAMES[d.getDay()] + ' ' + d.getDate() + ' ' + MONTHS[d.getMonth()] + '</span> <span>' + String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0') + '</span>';
}

// The whole body for a payload: the AI card, then every section in order, then (Head Office) Talk to Jack.
export function bodyHtml(payload, ai, aiBusy, adminObj) {
  const p = payload || {};
  const secs = p.sections || {};
  const ctx = { currency: p.currency === 'QAR' ? 'QAR' : 'AED' };
  let h = aiCardHtml(ai, aiBusy);
  SECTION_ORDER.forEach(([key, title]) => { h += sectionHtml(key, title, secs[key], ctx); });
  if (isHeadOffice(adminObj)) {
    h += '<div class="so-foot"><button type="button" class="btn btn-primary" id="storeOverviewJack" onclick="StoreOverview.talkToJack()">Talk to Jack about this store</button></div>';
  }
  return h;
}
function metaHtml(p) {
  if (!p) return '';
  const per = p.period || {};
  const bits = [];
  if (p.area) bits.push('<span translate="no">' + esc(p.area) + '</span>');
  if (p.country) bits.push('<span translate="no">' + esc(p.country) + '</span>');
  if (per.from) bits.push('<span>' + esc(dayLabel(per.from)) + '</span><span>-</span><span>' + esc(dayLabel(per.to)) + '</span>');
  if (per.basis === 'last_visit') bits.push(per.last_visit_date ? '<span>Since the visit on</span> <span>' + esc(dayLabel(per.last_visit_date)) + '</span>' : '<span>No visit on record: the last 7 days</span>');
  return bits.join('<span aria-hidden="true">·</span>');
}
function skeletonHtml() {
  const bar = (w) => '<span class="ui-skel" style="width:' + w + ';height:14px;margin:0 0 10px"></span>';
  let h = '';
  for (let i = 0; i < 4; i++) h += '<section class="so-card" aria-busy="true">' + bar('35%') + bar('100%') + bar('92%') + '</section>';
  return '<div aria-label="Loading the store overview">' + h + '</div>';
}

// ── The page ────────────────────────────────────────────────────────────────
export function render() {
  const el = root();
  if (!el) return;
  ensureCss();
  const stores = myStores();
  const head = '<div style="margin-bottom:12px"><div style="font-size:18px;font-weight:800">Store overview</div>' +
    '<div style="font-size:13px;color:var(--sub)">Everything about one store before a visit, on one page.</div></div>';
  if (!stores.length) {
    el.innerHTML = head + '<div class="ui-empty"><div class="ui-empty-title">No stores in your scope.</div><div>The store overview is for Head Office and area managers.</div></div>';
    return;
  }
  if (!_store || stores.indexOf(_store) === -1) {
    const saved = remembered();
    _store = (saved && stores.indexOf(saved) !== -1) ? saved : stores[0];
    _data = null; _ai = null; _week = null;
  }
  const groups = groupByArea(stores, areaOf, (typeof ALL_AREAS !== 'undefined') ? ALL_AREAS : []);
  const opts = groups.map((g) => '<optgroup label="' + esc(g.label) + '" translate="no">' + g.stores.map((s) =>
    '<option value="' + esc(s) + '"' + (s === _store ? ' selected' : '') + ' translate="no">' + esc(s) + '</option>').join('') + '</optgroup>').join('');
  el.innerHTML = head +
    '<div class="so-bar">' +
      '<select class="so-store" id="storeOverviewStore" aria-label="Store" onchange="StoreOverview.pickStore(this.value)">' + opts + '</select>' +
      '<button type="button" class="btn btn-outline" onclick="StoreOverview.refresh()">Refresh</button>' +
      '<button type="button" class="btn btn-primary" id="storeOverviewEmail" onclick="StoreOverview.emailMe()">Email me this</button>' +
    '</div>' +
    '<div class="so-bar" role="group" aria-label="Period">' + PERIODS.map((p) =>
      '<button type="button" class="so-period" data-period="' + p.key + '" aria-pressed="' + (p.key === _period ? 'true' : 'false') + '" onclick="StoreOverview.setPeriod(\'' + p.key + '\')">' + esc(p.label) + '</button>').join('') +
    '</div>' +
    '<div class="so-meta" id="storeOverviewMeta"></div>' +
    '<div id="storeOverviewBody"></div>';
  // v136.8: back on the tab within FRESH_MS, for the same store and period: draw what is here, ask nothing.
  if (_data && _loadedKey === loadKey() && (Date.now() - _loadedAt) < FRESH_MS) {
    const meta = byId('storeOverviewMeta');
    if (meta) meta.innerHTML = metaHtml(_data);
    drawBody();
    return;
  }
  load();
}

function drawBody() {
  const body = byId('storeOverviewBody');
  if (!body || !_data) return;
  body.innerHTML = bodyHtml(_data, _ai, _aiBusy, admin());
}
function aiKey() { return _store + '|' + _period; }
function loadKey() { const a = admin(); return (a && a.id != null ? a.id : '') + '|' + _store + '|' + _period + '|' + (_week || ''); }

export async function load() {
  const body = byId('storeOverviewBody');
  const meta = byId('storeOverviewMeta');
  if (!body || !_store) return;
  const my = ++_seq;
  const store = _store, period = _period, key = loadKey(), week = _week;
  body.innerHTML = skeletonHtml();
  if (meta) meta.innerHTML = '';
  let p;
  try {
    p = await api('/api/admin/store-overview?store=' + encodeURIComponent(store) + '&period=' + encodeURIComponent(period) + (_week ? '&week=' + encodeURIComponent(_week) : ''));
    if (!p || typeof p.sections !== 'object' || !p.sections) throw new Error('The server did not send an overview. Try again in a moment.');
  } catch (e) {
    if (my !== _seq) return;
    _data = null; _loadedKey = null;
    body.innerHTML = '<div class="so-card" role="alert" style="background:var(--danger-bg);color:var(--danger-deep)"><div style="font-weight:700">Could not load the store overview.</div><div>' + esc((e && e.message) || 'Unknown error') + '</div></div>';
    return;
  }
  if (my !== _seq) return;   // the store or period changed while this was loading
  _data = p;
  _loadedKey = key; _loadedAt = Date.now();   // v136.8
  _rotaShownWeek = week;
  if (_ai && _ai.key !== aiKey()) _ai = null;
  if (meta) meta.innerHTML = metaHtml(p);
  drawBody();
}

export function pickStore(name) {
  if (myStores().indexOf(name) === -1) return;   // only a store this person may see
  if (name !== _store) { _ai = null; _week = null; }
  _store = name;
  remember(name);
  load();
}
export function setPeriod(key) {
  if (!PERIODS.some((p) => p.key === key)) return;
  if (key !== _period) _ai = null;
  _period = key;
  if (typeof document !== 'undefined') {
    document.querySelectorAll('#storeOverviewRoot .so-period').forEach((b) => b.setAttribute('aria-pressed', b.getAttribute('data-period') === key ? 'true' : 'false'));
  }
  load();
}
export function refresh() { load(); }   // always asks again (v136.8: the tab itself may not)
// v136.8: forget when the last answer arrived, so the next tab open asks again (tests).
export function _forgetLoaded() { _loadedKey = null; _loadedAt = 0; }

// The rota card's previous / next week: the overview asked again with week= (the server answers that week's rota).
// v136.8 (the bugs audit, 16 Oct 2026): it re-ran ALL the sections (about 100 database calls) to move one card a
// week. Now it asks for the rota alone (sections=rota, which the server already supports) and replaces only the
// rota card; the rest of the page stays as it is. A failed step leaves the card on its week and says so.
let _rotaSeq = 0;   // the newest rota step; an older answer that lands late is dropped
let _rotaShownWeek;  // the _week the rota card on screen answers (set by load() and by a step that landed)
function rotaTitle() { const row = SECTION_ORDER.find((x) => x[0] === 'rota'); return row ? row[1] : 'Rota'; }
export async function rotaWeek(dir) {
  const r = _data && _data.sections && _data.sections.rota;
  const current = _week || (r && r.ok && r.data && r.data.week_start) || null;
  if (!current) return;
  const next = shiftWeek(current, dir);
  if (!next) return;
  const cardEl = (typeof document !== 'undefined') ? document.querySelector('#storeOverviewBody [data-sec="rota"]') : null;
  if (!_data || !cardEl) { _week = next; load(); return; }   // nothing on screen to patch: the whole page, as before
  const before = _week;
  _week = next;
  const my = ++_rotaSeq, seq = _seq, store = _store, period = _period;
  cardEl.setAttribute('aria-busy', 'true');
  let sec = null, failed = null;
  try {
    const p = await api('/api/admin/store-overview?store=' + encodeURIComponent(store) + '&period=' + encodeURIComponent(period) + '&week=' + encodeURIComponent(next) + '&sections=rota');
    sec = p && p.sections && p.sections.rota;
    if (!sec || typeof sec !== 'object') throw new Error('The server did not send the rota. Try again in a moment.');
  } catch (e) { failed = e; }
  // a newer step, a new whole load, or another store or period since: this answer is not for what is on screen
  if (my !== _rotaSeq || seq !== _seq || store !== _store || period !== _period || !_data) return;
  const live = document.querySelector('#storeOverviewBody [data-sec="rota"]');
  if (failed) {
    // back to the week the card shows (a quick second click can have moved _week past one never loaded)
    _week = (_rotaShownWeek !== undefined) ? _rotaShownWeek : before;
    if (live) live.removeAttribute('aria-busy');
    toast('Could not load that week: ' + ((failed && failed.message) || 'unknown error'), 'error');
    return;
  }
  _data.sections.rota = sec;
  _rotaShownWeek = next;
  if (_loadedKey !== null) _loadedKey = loadKey();   // the page now answers this week (the tab-reopen check)
  if (!live) return;
  const tmp = document.createElement('div');
  tmp.innerHTML = sectionHtml('rota', rotaTitle(), sec, { currency: _data.currency === 'QAR' ? 'QAR' : 'AED' });
  if (tmp.firstElementChild) live.replaceWith(tmp.firstElementChild);
}
// Store rotas, at this store (its own scope check runs in StoreRotas.pickStore).
export function openInStoreRotas() {
  if (!_store) return;
  try { if (typeof StoreRotas !== 'undefined' && StoreRotas && typeof StoreRotas.pickStore === 'function') StoreRotas.pickStore(_store); } catch (e) { /* the page opens on its own remembered store */ }
  const btn = tabButton('storerotas');
  if (typeof switchTab === 'function') switchTab('storerotas', btn);
}

async function askAi(refreshIt) {
  if (_aiBusy || !_store) return;
  const key = aiKey();
  _aiBusy = true;
  drawBody();
  try {
    const r = await api('/api/admin/store-overview/ai', { method: 'POST', body: { store: _store, period: _period, refresh: !!refreshIt } });
    if (key !== aiKey()) return;
    _ai = { key, key_points: arr(r.key_points), focus: arr(r.focus), cached: !!r.cached, generated_at: r.generated_at || null };
  } catch (e) {
    if (key !== aiKey()) return;
    _ai = { key, error: (e && e.message) || 'Unknown error' };
  } finally {
    _aiBusy = false;
    drawBody();
  }
}
export function writeAi() { return askAi(false); }
export function refreshAi() { return askAi(true); }

// The signed-in account's own email, from the accounts list (DB.accounts.list), or null if it cannot be read.
// '' means the account has no email on file.
async function myEmail() {
  const me = admin();
  if (!me || typeof DB === 'undefined' || !DB.accounts || typeof DB.accounts.list !== 'function') return null;
  try {
    const rows = await DB.accounts.list();
    const row = arr(rows).find((a) => a && me.id != null && String(a.id) === String(me.id)) ||
      (me.id == null ? arr(rows).find((a) => a && a.name === me.name) : null);
    if (!row) return null;
    return String(row.email || '').trim();
  } catch (e) { return null; }
}

export async function emailMe() {
  if (_emailBusy || !_store) return;
  const email = await myEmail();
  if (email === '') {
    toast('Your account has no email address. Ask Head Office to add one.', 'error');
    return;
  }
  const msg = (email ? 'Email this overview to ' + email + '?' : 'Email this overview to the address on your Shift account?') +
    '\n\nIt goes to your own address only, and it includes staff names.';
  const ask = (typeof window !== 'undefined' && typeof window.uiConfirm === 'function') ? window.uiConfirm : sharedConfirm;
  const ok = await ask(msg, { confirmLabel: 'Send' });
  if (!ok) return;
  _emailBusy = true;
  const btn = byId('storeOverviewEmail');
  if (btn) btn.disabled = true;
  try {
    const hasAi = !!(_ai && !_ai.error && _ai.key === aiKey() && (arr(_ai.key_points).length || arr(_ai.focus).length));
    const r = await api('/api/admin/store-overview/email', { method: 'POST', body: { store: _store, period: _period, include_ai: hasAi } });
    toast('Sent to ' + (r.to || email || 'your email'), 'success');
  } catch (e) {
    toast('Could not send: ' + ((e && e.message) || 'Unknown error'), 'error');
  } finally {
    _emailBusy = false;
    if (btn) btn.disabled = false;
  }
}

// Head Office only (Ask JACK is Head Office only on the server): opens the JACK page with a starter question about
// this store in the box. Nothing is sent: the person reads it and presses Send.
export function talkToJack() {
  if (!_store || !isHeadOffice(admin())) return;
  const btn = tabButton('jack');
  if (typeof switchTab === 'function') switchTab('jack', btn);
  const inp = byId('jackChatInput');
  if (inp) {
    inp.value = jackQuestion(_store);
    try { inp.focus(); } catch (e) { /* not focusable here */ }
  }
}

// ── Open a store visit's report (the Store visits tab, src/storeVisits openVisitDetail via window.openStoreVisitDetail)
// Only what the person could open by clicking: the Store visits tab must be one they can see, and the visit's store must
// be in their scope. The overview's own button already knows the store (the one on screen, which the server scoped);
// a link from outside (admin.html?tab=store-visits&visit=<id>, or the monthly email's ?visit=<id>) reads the visit
// first to learn its store. Returns true when the report was opened.
function visitsTabButton() {
  return tabButton('store-visits');
}
function canSeeVisitsTab() {
  const b = visitsTabButton();
  return !!(b && b.style.display !== 'none');
}
function showVisit(id) {
  const btn = visitsTabButton();
  if (typeof switchTab === 'function') switchTab('store-visits', btn);
  if (typeof window !== 'undefined' && typeof window.openStoreVisitDetail === 'function') { window.openStoreVisitDetail(id); return true; }
  return false;
}
export function openVisit(id) {
  const vid = String(id || '');
  if (!VISIT_ID_RE.test(vid) || !canSeeVisitsTab()) return false;
  // The visit belongs to the store on screen: it came from that store's overview.
  if (!_store || myStores().indexOf(_store) === -1) return false;
  return showVisit(vid);
}
export async function openVisitLink(id) {
  const vid = String(id || '');
  if (!VISIT_ID_RE.test(vid) || !canSeeVisitsTab()) return false;
  let row;
  try { row = await api('/api/store-visits/' + encodeURIComponent(vid)); }
  catch (e) { toast('Could not open that visit: ' + ((e && e.message) || 'Unknown error'), 'error'); return false; }
  let areaMap = {};
  try { areaMap = (typeof AREA_STORE_MAP !== 'undefined') ? AREA_STORE_MAP : {}; } catch (e) { areaMap = {}; }
  // v136.9: a visit you wrote yourself opens even when its store is outside your area (read only, your own work)
  const me = admin();
  const ownVisit = !!(row && me && me.id != null && row.visited_by_id != null && String(row.visited_by_id) === String(me.id));
  if (!row || (!visitInScope(row.store, me, areaMap) && !ownVisit)) {
    toast('That visit is not in your scope.', 'error');
    return false;
  }
  return showVisit(vid);
}

// For the tests: the page's state, read only.
export function _state() { return { store: _store, period: _period, week: _week, ai: _ai, data: _data }; }
